import { SentinelRootProjectId } from '@tableau/nested-project-explorer';
import _ from 'underscore';

import {
    createMultipleItemResultNotificationValues, MultipleItemResultNotificationValues
} from '~commonInfra/contentActions/ActionMessages';
import { ContentActionNotification } from '~commonInfra/contentActions/ContentActionNotification';
import { IErrorItem, MultipleItemResult } from '~commonInfra/contentActions/MultipleItemResult';
import { openReactActionDialog } from '~commonInfra/dialogs/ReactActionDialog';
import { openReactAlertDialog } from '~commonInfra/dialogs/ReactAlertDialog';
import { TabDialogWidth } from '~commonInfra/dialogs/style/TabDialogWidth';
import { Set } from '~commonInfra/utils/Set';
import { emitAdminInsightsContentEvent } from '~src/adminInsights/telemetry/AdminInsightsPftEmitter';

import { Formatters, Messages } from '../../codegen/Localize';
import { L10n } from '../../commonInfra/L10n/L10n';
import { IDataSourceBase } from '../../datasource/models/IDataSourceInfo';
import { DataSources } from '../../datasource/resources/Datasources';
import { DataSourceErrors } from '../../datasource/services/DataSourceErrors';
import { FlowBaseInfo } from '../../flow/models/FlowInfo';
import { FlowErrors } from '../../flow/services/FlowErrors';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { Projects } from '../../projects/resources/Projects';
import {
    DatasourceContentType, FlowContentType, IContentType,
    ProjectContentType, WorkbookContentType
} from '../../spacesPlatform/models/ContentTypes';
import { INamedItemInfo } from '../../spacesPlatform/models/resourceInfoParts';
import { IWorkbookBase } from '../../workbook/models/IWorkbookInfo';
import { Workbooks } from '../../workbook/resources/Workbooks';
import { WorkbookErrors } from '../../workbook/services/WorkbookErrors';
import { MoveToProjectDialogBody, MoveToProjectDialogBodyProps, MoveToProjectDialogBodyState } from '../components/MoveToProjectDialogBody';
import { IProjectBase } from '../models/IProjectInfo';
import { IProjectName } from '../ServerApi/types/IProject';
import { invalidateProjectQueries, projectQueryKeys } from '../services/Queries';
import { ProjectErrors } from './ProjectErrors';

export interface ITranslationIds {
  promptId: (a: {
    ITEM_COUNT: number;
    ITEM_COUNT_LOC: string;
    ITEM_NAME: string;
  }) => string;
  successId: (a: {
    ITEM_COUNT: number;
    ITEM_COUNT_LOC: string;
    PROJECT_NAME: string;
  }) => string;
  errorId: (a: {
    ERROR_COUNT: number;
    ERROR_COUNT_LOC: string;
    PROJECT_NAME: string;
  }) => string;
  dialogTitle: (a: {
    ITEM_COUNT: number;
    ITEM_COUNT_LOC: string;
  }) => string;
  confirmationText: (a: {
    ITEM_COUNT: number;
    ITEM_COUNT_LOC: string;
  }) => string;
}

export interface IResultWithDestinationProject extends IResult {
  destinationProjectId: string;
}

export const moveProjectsToProjectFn = (destinationProjectId: string, projectIds: string[]) => {
  return Projects.moveToProject(destinationProjectId, projectIds).then(results => {
    invalidateProjectQueries(projectQueryKeys.all());
    return results;
  });
};

export namespace MoveToProjectAction {

  export function moveProjects(projects: Set<IProjectBase>): Promise<IResult> {
    return move(
      {
        promptId: Messages.selectProjectForProjects,
        successId: Messages.moveProjectsToProjectSuccess,
        errorId: Messages.moveProjectsToProjectError,
        dialogTitle: Messages.moveProject,
        confirmationText: Messages.confirmMoveProject
      },
      projects,
      ProjectContentType,
      moveProjectsToProjectFn);
  }

  export function moveWorkbooks(workbooks: Set<IWorkbookBase>): Promise<IResultWithDestinationProject> {
    return move(
      {
        promptId: Messages.selectProjectForWorkbooks,
        successId: Messages.moveWorkbooksToProjectSuccess,
        errorId: Messages.moveWorkbooksToProjectError,
        dialogTitle: Messages.moveToProject,
        confirmationText: Messages.confirmMoveToProject
      },
      workbooks,
      WorkbookContentType,
      Workbooks.moveToProject);
  }

  export function moveDatasources(datasources: Set<IDataSourceBase>): Promise<IResult> {

    // Disallow move if all selected datasources are embedded.
    if (datasources.all((ds: IDataSourceBase) => _.isObject(ds.workbook))) {
      return Promise.resolve(openReactAlertDialog({
        title: Messages.moveDatasourcesToProject_cannotMoveAlertTitle(),
        message: Messages.moveDatasourcesToProject_cannotMoveAlertMessage()
      }).result);
    }

    return move(
      {
        promptId: Messages.selectProjectForDatasources,
        successId: Messages.moveDatasourcesToProjectSuccess,
        errorId: Messages.moveDatasourcesToProjectError,
        dialogTitle: Messages.moveToProject,
        confirmationText: Messages.confirmMoveToProject
      },
      datasources,
      DatasourceContentType,
      DataSources.moveToProject);
  }

  export function move(
    translations: ITranslationIds,
    items: Set<INamedItemInfo>,
    itemType: IContentType,
    moveFn: (projectId: string, itemIds: string[]) => Promise<any>): Promise<IResultWithDestinationProject> {

    const actionDisabled = (bodyProps: MoveToProjectDialogBodyProps, dialogState: MoveToProjectDialogBodyState) => {
      return dialogState.selectedProject === null;
    };

    const dialogInstance = openReactActionDialog<MoveToProjectDialogBodyProps, MoveToProjectDialogBodyState, IResultWithDestinationProject>({
      title: translations.dialogTitle({
        ITEM_COUNT: items.size(),
        ITEM_COUNT_LOC: Formatters.formatCount(items.size())
      }),
      initialBodyProps: {
        items,
        itemType,
        moveFn,
        translations
      },
      initialDialogState: {
        selectedProject: null
      },
      bodyComponent: MoveToProjectDialogBody,
      actionName: translations.confirmationText({
        ITEM_COUNT: items.size(),
        ITEM_COUNT_LOC: Formatters.formatCount(items.size())
      }),
      action: moveAndNotify,
      actionDisabled,
      isActionDelete: false,
      dismissDialogOnActionFailed: true,
      dialogWidth: TabDialogWidth.width375px
    });

    return Promise.resolve(dialogInstance.result);
  }

  export async function moveAndNotify(
    bodyProps: MoveToProjectDialogBodyProps,
    dialogState: MoveToProjectDialogBodyState
  ): Promise<IResultWithDestinationProject> {

    const destinationProjectId = getDestinationProjectIdFromDialog(dialogState.selectedProject.id);

    // Fire-and-forget Admin Insights PFT telemetry. Emit BEFORE the move so we still
    // have each item's source project id (post-move, project membership has changed).
    try {
      bodyProps.items.each((item: any) => {
        const ct = item.contentType;
        if (ct !== 'workbook' && ct !== 'datasource') { return; }
        const projectId = item.project?.id;
        emitAdminInsightsContentEvent(
          ct as 'workbook' | 'datasource',
          { projectId, luid: item.luid, name: item.name },
          'move',
        );
      });
    } catch {
      // Telemetry must never disrupt the primary user flow
    }

    const result = await bodyProps.moveFn(destinationProjectId, bodyProps.items.keys());

    ContentActionNotification.displayItemizedToastsForResult(
      notification(
        new MultipleItemResult(bodyProps.items, result),
        bodyProps.translations,
        bodyProps.itemType,
        dialogState.selectedProject
      )
    );

    return { ...result, destinationProjectId: dialogState.selectedProject };
  }

  function getDestinationProjectIdFromDialog(destinationProjectId: string): string {
    return (destinationProjectId === SentinelRootProjectId ? null : destinationProjectId);
    // API expects a null ID if we're moving a project to the top level of the site
  }

  export function notification(
    result: MultipleItemResult<INamedItemInfo>,
    translations: ITranslationIds,
    itemType: IContentType,
    project: IProjectName): MultipleItemResultNotificationValues {

    return createMultipleItemResultNotificationValues(result,
      {
        describeSuccessSummary: (successCount: number) =>
          translations.successId({
            ITEM_COUNT: successCount,
            ITEM_COUNT_LOC: Formatters.formatCount(successCount),
            PROJECT_NAME: L10n.elideIfLong(project.name)
          }),

        describeErrorSummary: (errorCount: number) =>
          translations.errorId({
            ERROR_COUNT: errorCount,
            ERROR_COUNT_LOC: Formatters.formatCount(errorCount),
            PROJECT_NAME: L10n.elideIfLong(project.name)
          }),

        describeErrorDetail: (errorItem: IErrorItem<INamedItemInfo>) => {
          if (!errorItem.item || !errorItem.error) return;
          if (itemType === ProjectContentType) {
            return ProjectErrors.errorDetailsFor(<IErrorItem<IProjectBase>>errorItem);
          } else if (itemType === DatasourceContentType) {
            return DataSourceErrors.errorDetailsFor(<IErrorItem<IDataSourceBase>>errorItem);
          } else if (itemType === WorkbookContentType) {
            return WorkbookErrors.errorDetailsFor(<IErrorItem<IWorkbookBase>>errorItem);
          } else if (itemType === FlowContentType) {
            return FlowErrors.errorDetailsFor(<IErrorItem<FlowBaseInfo>>errorItem);
          }
        }
      }
    );
  }

}

export type MoveToProjectAction = typeof MoveToProjectAction;
